<template>
  <div class="wt-asset-editor-view js-modal-content">
    <layout-general :header-height="50">
      <span slot="top-right-floating" />
      <div class="-header" slot="header">
        <span
          v-text="
            openedFrom == 'timeline-change-segment'
              ? 'Re-Trim Timeline Segment'
              : 'Video Preview'
          "
        />
        <div class="-spring" />
        <ian-controls />
        <div class="-divider" />
        <wt-close class="-close" @click.native="$emit('close')" />
      </div>
      <div slot="top" />

      <div class="-main" slot="main">
        <wt-player-video
          @play="videoPlayed"
          :toggle-play-on-space="!isFragmentEditorDragging"
        >
          <wt-volume-control group="video_modal">
            <video
              ref="video"
              playsinline
              preload="metadata"
              :poster="posterUrl"
              :src="videoUrl"
            />
          </wt-volume-control>
          <img
            v-if="maskSrc"
            slot="overlay-layer"
            class="-mask-image"
            :src="mixinSrc(maskSrc)"
          />
        </wt-player-video>
      </div>

      <scene-detection
        v-if="showSceneDetection"
        slot="bottom"
        class="-scene-detection"
        :src="mixinSrc(src)"
        :selected-scene.sync="selectedScene"
        :scene-at-playhead="sceneAtPlayhead"
        @select="selectScene"
        @scenes-update="scenesUpdated"
      />

      <info-bar
        :asset="workModel"
        slot="left"
        @close="close"
        :videoTrimming="true"
      >
        <template v-if="showTranscribe" v-slot:transcription>
          <asset-preview-inline-transcription
            ref="transcribe"
            class="-transcribe-container"
            :asset="workModel"
            @seek-preview-video="$_setVideoTime"
            @range-selection="$_rangeSelection"
            :playbackTime="videoTime"
          />
        </template>
      </info-bar>

      <div slot="pre-footer">
        <div class="-fragment-wrapper">
          <div class="-fragment-wrapper-label">
            <span>{{ 'assetEditor.segmentsTitle' | culture }}</span>
            <span
              v-if="!selectedAllVideo"
              class="-select-all wt2-color-link"
              @click="resetScene(true)"
            >
              {{ 'assetEditor.resetSegment' | culture }}
            </span>
          </div>

          <keyboard-tips />
          <fragment-editor
            v-if="isMounted"
            v-model="fragmentValue"
            ref="fragmentEditor"
            :video-based="true"
            :class="{ '-video-not-ready': !videoDuration }"
            :min-fragment-duration="minSelectableVideoSegmentTime"
            :snap-points="snapPoints"
            :playhead-cmp="Playhead"
            :sprite-cmp="Sprite"
            :selection-cmp="Selection"
            :markers-cmp="Markers"
            :get-current-time="getVideoTime"
            :media-duration="videoDuration"
            :seeked-time="seekedTime"
            :paused="mediaPaused"
            @play="$_goPlayVideo"
            @pause="$_goPauseVideo"
            @set-time="$_onSetTime"
            @playhead-update="onPlayheadUpdate"
            @analytics-event="fragmentEditorEvent"
            @seek-frames="$_onSeekFrames"
            @toggle-dragging="isFragmentEditorDragging = $event"
          />
        </div>
      </div>

      <video-trimming-apply-cancel
        slot="footer"
        :selectedAllVideo="selectedAllVideo"
        :applyDisabled="!!!videoDuration"
        :openedFrom="openedFrom"
        @addFragment="addFragment"
        @close="close"
      />
    </layout-general>
  </div>
</template>

<script>
import LayoutGeneral from 'AssetEditor/layouts/LayoutGeneral.vue';
import VideoTrimmingApplyCancel from './functionality/VideoTrimming/VideoTrimmingApplyCancel.vue';
import SceneDetection from './functionality/VideoTrimming/SceneDetection.vue';
import FragmentEditor from '@wochitnpm/fragment-editor/src/FragmentEditor.vue';
import PlayheadWithTime from '@wochitnpm/fragment-editor/src/strategies/Playhead/WithTime.vue';
import SpriteFromVideo from '@wochitnpm/fragment-editor/src/strategies/Sprite/FromVideo.vue';
import SpriteFromMetadata from '@wochitnpm/fragment-editor/src/strategies/Sprite/FromMetadata.vue';
import SelectionWithDuration from '@wochitnpm/fragment-editor/src/strategies/Selection/WithDuration.vue';
import Markers from '@wochitnpm/fragment-editor/src/strategies/Markers/index.vue';
import hoc from '@wochitnpm/fragment-editor/src/strategies/hoc';
import InfoBar from 'v/AssetPreview/InfoBar.vue';
import KeyboardTips from './info/KeyboardTips.vue';
import IanControls from './functionality/InAssetNavigation/IanControls.vue';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import app from 'app';
import bus from 'v/AssetEditor/AssetEditorEventBus';
import KeyboardController from './functionality/VideoTrimming/KeyboardController';
import Analytics from 'c/WtAnalytics';
import fg from '#/libs/FrameGrid';
import AssetPreviewInlineTranscription from 'v/AssetPreview/assetPreviewInlineTranscription.vue';
import { isAssetCandidateForTranscribe } from 'v/Story/Timeline/api/transcription';

export default {
  name: 'video-trimming',
  components: {
    VideoTrimmingApplyCancel,
    SceneDetection,
    InfoBar,
    LayoutGeneral,
    FragmentEditor,
    AssetPreviewInlineTranscription,
    KeyboardTips,
    IanControls,
  },

  data() {
    return {
      isMounted: false,
      scenes: [],
      videoDuration: 0,
      playheadTime: 0,
      fragmentValue: {
        start: 0,
        end: 0,
      },
      isFragmentEditorDragging: false,
      selectedScene: null,
      sceneAtPlayhead: null,
      seekedTime: 0,
      mediaPaused: true,
      videoTime: 0,
      showTranscribe: false,
    };
  },

  beforeDestroy() {
    this.$options.keyboardController?.unregister();
    bus.$off('SCENE_ARROW_NAVIGATE_LEFT', this.$_onSceneArrowNavigateLeft);
    bus.$off('SCENE_ARROW_NAVIGATE_RIGHT', this.$_onSceneArrowNavigateRight);
    this.$refs.video.removeEventListener('playing', this.$_videoPlayPause);
    this.$refs.video.removeEventListener('pause', this.$_videoPlayPause);
    this.$refs.video.removeEventListener('seeked', this.$_videoSeeked);
    window.clearInterval(this.playInterval);
    this.playInterval = null;
  },

  created() {
    bus.$on('SCENE_ARROW_NAVIGATE_LEFT', this.$_onSceneArrowNavigateLeft);
    bus.$on('SCENE_ARROW_NAVIGATE_RIGHT', this.$_onSceneArrowNavigateRight);
  },

  mounted() {
    if (this.$refs.video) {
      // enable immediate segment selection
      if (this.workModel?.metadata?.duration) {
        this.videoDuration = fg.trunc(this.workModel.metadata.duration);
        this.fragmentValue.end = this.videoDuration;
      }

      this.$refs.video.addEventListener('loadedmetadata', this.$_videoLoaded);
      this.$refs.video.addEventListener('playing', this.$_videoPlayPause);
      this.$refs.video.addEventListener('pause', this.$_videoPlayPause);
      this.$refs.video.addEventListener('seeked', this.$_videoSeeked);

      this.$options.keyboardController = new KeyboardController({
        host: this,
        audioSync: true,
      });
      this.$options.keyboardController.register();
      this.$nextTick(() => {
        this.isMounted = true;
      });
    } else {
      throw new Error('fragment editor require video html element');
    }

    this.$nextTick(() => {
      document.querySelector('.wt2-asset-modal')?.classList.remove('preview');
    });

    setTimeout(() => {
      //we delay because of the modal transition animations causing bugs while rendering tokens.
      if (this._isMounted && this.isAssetCandidateForTranscribe) {
        this.showTranscribe = true;
      }
    }, 350);
  },

  computed: {
    ...mapGetters('assetEditor', ['workModel']),
    ...mapState({
      selectedAssets: (state) => state.story.selectedAssets,
      minSelectableVideoSegmentTime: (state) =>
        state.assetEditor.story.programmer.minSelectableVideoSegmentTime,
      openedFrom: (state) => state.assetEditor.openedFrom,
    }),

    hasChanged() {
      return (
        (this.workModel.startTime || this.workModel.endTime) &&
        (this.fragmentValue.start !== this.workModel.startTime ||
          this.fragmentValue.end !== this.workModel.endTime)
      );
    },

    selectedAllVideo() {
      return (
        this.fragmentValue.start === 0 &&
        this.fragmentValue.end === this.videoDuration
      );
    },

    isAssetCandidateForTranscribe() {
      return isAssetCandidateForTranscribe(this.workModel);
    },

    maskSrc() {
      return this.workModel._staticMaskPreview;
    },

    src() {
      return (
        this.workModel.metadata.urlCdn ||
        this.workModel.metadata.providerLowResAssetUrl ||
        this.workModel.metadata.providerHiResAssetUrl
      );
    },

    posterUrl() {
      if (app.assetType.isINSTAGRAM(this.workModel)) {
        return this.mixinProxySrc(this.workModel.metadata.posterUrl);
      }
      return this.mixinSrc(this.workModel.metadata.posterUrl);
    },

    videoUrl() {
      if (app.assetType.isINSTAGRAM(this.workModel)) {
        return this.mixinProxySrc(this.src);
      }
      return this.mixinSrc(this.src);
    },

    snapPoints() {
      let rv = [];
      const scenes = _.isEmpty(this.scenes) ? [] : this.scenes;

      // populate `rv` with unique points, mark each one with snapping ability flag
      for (let i = 0, I = scenes.length, scene, lastPoint; i < I; i++) {
        scene = scenes[i];
        const endTime = app.sfp(scene.startTime + scene.duration, 1e3);
        const startPoint = {
          time: scene.startTime,
          forStart: true,
        };
        const endPoint = {
          time: endTime,
          forEnd: true,
        };

        if (lastPoint) {
          if (lastPoint.time === startPoint.time) {
            lastPoint.forStart = true;
          } else {
            rv.push(startPoint);
          }
        } else {
          rv.push(startPoint);
        }

        rv.push(endPoint);

        lastPoint = endPoint;
      }

      if (rv.length) {
        // trim points on video edges
        if (rv[0].time === 0) {
          rv = rv.slice(1);
        }
        if (rv[rv.length - 1].time === this.videoDuration) {
          rv = rv.slice(0, rv.length - 1);
        }
      }

      return rv;
    },

    Playhead() {
      return PlayheadWithTime;
    },

    Selection() {
      return SelectionWithDuration;
    },

    showSceneDetection() {
      return (
        !app.assetType.isSOCIALVIDEO(this.workModel) &&
        !app.assetType.isDynamicProvider(this.workModel)
      );
    },

    Sprite() {
      if (
        this.workModel.metadata.videoAssetStoryBoard &&
        // when the duration is less then 25 sec the sprite from the server
        // isn't long enough for the sprite. Therefore the sprite need to load from the video
        this.workModel.metadata.duration >=
          app.TIME.MIN_VIDEO_DURATION_FOR_BACKEND_SPRITE
      ) {
        return hoc(SpriteFromMetadata, {
          src: this.mixinSrc(
            this.workModel.metadata.videoAssetStoryBoard.links[0]
          ),
          width: this.workModel.metadata.videoAssetStoryBoard.width,
          height: this.workModel.metadata.videoAssetStoryBoard.height,
        });
      } else {
        return hoc(SpriteFromVideo, {
          src: this.videoUrl,
        });
      }
    },

    Markers() {
      if (this.openedFrom === 'timeline-change-segment') {
        return hoc(Markers, {
          markers: [
            {
              startTime: this.workModel.startTime,
              endTime: this.workModel.endTime,
            },
          ],
        });
      }
      return hoc(Markers, {
        markers: this.selectedAssets
          .filter(
            (a) => this.workModel.metadata.assetUUID === a.metadata.assetUUID
          )
          .map(({ startTime, endTime }) => ({ startTime, endTime })),
      });
    },
  },

  watch: {
    fragmentValue: {
      immediate: true,
      deep: true,
      handler(fragmentValue) {
        this.selectedScene = this.$_findSceneBySelection(fragmentValue);
      },
    },

    fragmentValue: {
      deep: true,
      immediate: false,
      handler(fragmentValue) {
        if (this.isMounted && fragmentValue) {
          if (this.$refs.transcribe) {
            this.$refs.transcribe.selectByTimestamps(
              fragmentValue.start,
              fragmentValue.end - 0.001
            );
          }
        }
      },
    },

    selectedScene: {
      deep: true,
      handler: _.debounce(
        function (selectedScene) {
          if (selectedScene) {
            if (this.$refs.transcribe) {
              this.$refs.transcribe.selectByTimestamps(
                selectedScene.startTime,
                selectedScene.startTime + selectedScene.duration - 0.001
              );
            }
          }
        },
        app.TIME.MENTAL.λ,
        { leading: true, trailing: true }
      ),
    },

    scenes: {
      deep: false,
      handler() {
        this.selectedScene = this.$_findSceneBySelection(this.fragmentValue);
      },
    },

    playheadTime(newValue) {
      if ((!newValue && newValue !== 0) || !this.$refs.video.paused) {
        this.sceneAtPlayhead = null;
      }
      this.sceneAtPlayhead = this.$_findSceneByTime(newValue);
    },
  },

  methods: {
    ...mapActions(['ASSET_SELECTED']),
    ...mapMutations('assetEditor', ['setMetadataDuration']),

    $_videoPlayPause() {
      this.mediaPaused = this.$refs.video.paused;
      if (this.mediaPaused) {
        this.$_goPauseVideo();
        window.clearInterval(this.playInterval);
        this.playInterval = null;
      } else {
        this.playInterval = window.setInterval(
          this.$_timeupdate.bind(this),
          25
        );
      }
    },

    $_videoSeeked() {
      this.seekedTime = this.getVideoTime();
    },

    $_timeupdate(time) {
      this.videoTime = this.getVideoTime();
    },

    fragmentEditorEvent(o) {
      if (o.name === 'durationButtonPlay') {
        Analytics.trigger('analytics:videoTrimming:playVideo', {
          using: 'Duration button',
        });
      }
    },

    $_onSceneArrowNavigateLeft() {
      if (this.selectedScene) {
        this.$options.keyboardController.seekScenes(null, { left: true });
      } else {
        const time = this.$refs.video.currentTime;
        const scene = this.$_findSceneByTime(time, { left: true });
        this.selectScene(scene);
      }

      this.$nextTick(() => {
        bus.$emit('ENSURE_SELECTED_SCENE_PAGE_VISIBLE');
      });
    },

    $_onSceneArrowNavigateRight() {
      if (this.selectedScene) {
        this.$options.keyboardController.seekScenes(null, { right: true });
      } else {
        const time = this.$refs.video.currentTime;
        const scene = this.$_findSceneByTime(time, { right: true });
        this.selectScene(scene);
      }

      this.$nextTick(() => {
        bus.$emit('ENSURE_SELECTED_SCENE_PAGE_VISIBLE');
      });
    },

    $_findSceneBySelection({ start, end }) {
      return _.find(this.scenes, (scene) => {
        const sceneEndTime = app.sfp(scene.startTime + scene.duration, 1e3);
        return scene.startTime === start && end === sceneEndTime;
      });
    },

    $_findSceneByTime(time, { left, right } = {}) {
      // enclosing
      let rv = _.find(this.scenes, (scene, i) => {
        const sceneEndTime = app.sfp(scene.startTime + scene.duration, 1e3);
        const isLast = i === this.scenes.length - 1;
        return (
          scene.startTime <= time &&
          (isLast ? time <= sceneEndTime : time < sceneEndTime)
        );
      });

      if (!rv) {
        if (left) {
          rv = _.findLast(this.scenes, (scene) => {
            const sceneEndTime = app.sfp(scene.startTime + scene.duration, 1e3);
            return sceneEndTime < time;
          });
          // last
          if (!rv) {
            rv = this.scenes[this.scenes.length];
          }
        } else if (right) {
          // next from right
          rv = _.find(this.scenes, (scene) => {
            const sceneEndTime = app.sfp(scene.startTime + scene.duration, 1e3);
            return sceneEndTime > time;
          });
          // first
          if (!rv) {
            rv = this.scenes[0];
          }
        }
      }

      return rv;
    },

    $_videoLoaded() {
      this.$refs.video.removeEventListener(
        'loadedmetadata',
        this.$_videoLoaded
      );
      this.videoDuration = fg.trunc(this.$refs.video.duration);
      this.$_trimScenesToVideoDuration(this.scenes);
      this.setMetadataDuration(this.videoDuration);

      if (
        app.assetType.isVIDEO(this.workModel) &&
        Object.getOwnPropertyDescriptor(this.workModel, 'startTime') &&
        Object.getOwnPropertyDescriptor(this.workModel, 'endTime') &&
        this.workModel.startTime !== this.workModel.endTime
      ) {
        this.fragmentValue.start = this.workModel.startTime;
        this.fragmentValue.end = this.workModel.endTime;
      } else {
        this.resetScene();
      }
    },

    onPlayheadUpdate(time) {
      this.playheadTime = time;
      this.videoTime = time;
    },

    scenesUpdated(scenes) {
      this.$_trimScenesToVideoDuration(scenes);
      this.scenes = scenes;
    },

    $_goPlayVideo() {
      this.$refs.video.play().catch(() => 0);
    },

    $_goPauseVideo() {
      this.$refs.video.pause();
    },

    $_onSetTime(time) {
      this.$refs.video.currentTime = time;
    },

    $_trimScenesToVideoDuration(scenes) {
      const lastScene = _.last(scenes);
      if (lastScene && this.videoDuration) {
        const lastSceneEndTime = app.sfp(
          lastScene.startTime + lastScene.duration,
          1e3
        );
        if (lastSceneEndTime > this.videoDuration) {
          lastScene.duration = app.sfp(
            this.videoDuration - lastScene.startTime,
            1e3
          );
        }
      }
    },

    resetScene(sendEvent = false) {
      this.fragmentValue.start = 0;
      this.fragmentValue.end = this.videoDuration;
      if (sendEvent) {
        Analytics.trigger('analytics:videoTrimming:resetScene');
      }
    },

    selectScene(scene) {
      this.selectedScene = scene;
      this.fragmentValue.start = scene.startTime;
      this.fragmentValue.end = app.sfp(scene.startTime + scene.duration, 1e3);
      this.$_setVideoTime(this.fragmentValue.start);
    },

    $_setVideoTime(time) {
      this.$refs.video.currentTime = time;
      this.seekedTime = time;
    },

    $_rangeSelection({ startTime, endTime }) {
      this.fragmentValue.start = startTime;
      this.fragmentValue.end = endTime;
    },

    getVideoTime() {
      return this.$refs?.video?.currentTime || 0;
    },

    videoPlayed(source) {
      Analytics.trigger('analytics:videoTrimming:playVideo', { using: source });
    },

    addFragment({ andClose }) {
      if (!this.videoDuration) {
        return;
      }

      const fragment = Object.assign(_.cloneDeep(this.workModel), {
        dateSelected: Date.now(),
        startTime: this.fragmentValue.start,
        endTime: this.fragmentValue.end,
        type: app.assetType.isSOCIAL(this.workModel)
          ? app.assetType.SOCIALFRAGMENT
          : app.assetType.FRAGMENT,
      });
      fragment.id = app.generateAssetId(fragment);
      if (fragment.type === app.assetType.FRAGMENT) {
        fragment.metadata.assetType = 'VIDEO_SEGMENT';
      }

      Analytics.trigger('analytics:videoTrimming:addSegment', {
        selection: this.selectedAllVideo
          ? 'All'
          : this.selectedScene
          ? 'Detected Scene'
          : 'Segment',
      });

      const transcriptLanguageCode =
        this.$refs.transcribe?.getTranscriptionCode();

      if (this.openedFrom === 'timeline-change-segment') {
        this.close({ asset: fragment });
      } else {
        this.ASSET_SELECTED({ asset: fragment, transcriptLanguageCode })
          .then(() => {
            bus.$emit('fragment-added');
          }, _.noop)
          .finally(() => {
            if (andClose) {
              this.close();
            } else {
              this.$_markNextFragment();
            }
          });
      }
    },

    $_markNextFragment() {
      if (this.selectedScene) {
        this.$options.keyboardController.seekScenes(null, { right: true });
      } else {
        if (this.fragmentValue.end < this.videoDuration) {
          this.fragmentValue.start = this.fragmentValue.end;
          this.fragmentValue.end = this.videoDuration;
        }
      }
    },

    close(e) {
      this.$emit('close', e);
    },

    $_onSeekFrames(e) {
      this.$options?.keyboardController.seekFrames(null, e);
    },
  },
};
</script>

<style lang="scss" scoped>
.-header {
  height: 100%;
  padding-left: 20px;
  display: flex;
  align-items: center;

  .-spring {
    flex-grow: 1;
  }

  .-divider {
    border-left: 1px solid rgba(0, 0, 0, 0.2);
    height: 100%;
  }

  .-close {
    width: 50px;
  }
}

.-main {
  height: 100%;

  .-mask-image {
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
    pointer-events: none;
  }
}

.-fragment-wrapper {
  position: relative;
  height: 60px;
  width: calc(100% - 60px);
  margin: 68px auto 0;

  .-fragment-wrapper-label {
    position: absolute;
    top: -53px;
    left: 0;
    font-size: 14px;
  }

  .-select-all {
    margin-left: 14px;
  }

  .-fragment-editor {
    &.-video-not-ready {
      pointer-events: none;
    }
  }
}
</style>
